/**
 * Újdonságok a Funkciók oldalon - a legfrissebb elöl.
 *
 * Minden felhasználónak látható változásnál (új funkció, fontos javítás) ide
 * kerül egy bejegyzés, a felhasználó nyelvén, nem commit-üzenetként. Ami
 * új funkció, annak a features.ts-ben is van sora, és a `feature` mező arra mutat.
 */
export interface ReleaseItem {
  text: string;
  /** A features.ts funkciója, ha van - a Funkciók oldal ide linkel. */
  feature?: string;
}

export interface Release {
  /** ÉÉÉÉ-HH-NN */
  date: string;
  title: string;
  items: ReleaseItem[];
  /** Kinek szól a kezdőlapi banner: 'staff' = csak adminnak és edzőnek. Alapból mindenkinek. */
  audience?: 'all' | 'staff';
  /** Csak akkor hirdessük, ha az AI be van kapcsolva a klubban. */
  requiresAi?: boolean;
}

export const releases: Release[] = [
  {
    date: '2026-10-22',
    title: 'Biztonságosabb belépés, jobb adatvédelem',
    items: [
      { text: 'Az új jelszó legalább 8 karakter, és betű és szám is van benne; a túl gyakori jelszavakat a rendszer nem fogadja el.' },
      { text: '5 sikertelen belépési próbálkozás után a fiók 15 percre zárolódik - így a jelszót nem lehet végigpróbálgatni. Ha elfelejtetted, kérj új jelszót.' },
      { text: 'A betegség- és sérülésbejelentés megjegyzését csak a stáb és a saját család látja, a csapattársak és a szüleik nem.' },
      { text: 'A szülők és játékosok csak a saját adatlapjukat látják; a csapattársak személyes adatai (pl. anyja neve, születési hely) rejtve maradnak.' },
      { text: 'Az új fióknak küldött levélben jelszó helyett egy jelszóbeállító link van.' },
      { text: 'Egy esemény vagy játékos megnyitásakor a böngésző címe is megváltozik, így a link megosztható, és frissítés után is ott marad az ablak. Az Esc billentyű minden ablakot bezár, egymásba nyitott ablakoknál mindig csak a legfelsőt.' },
      { text: 'A családi app kezdőlapja mutatja, ha a gyerek beteg vagy sérült, és hogy emiatt automatikusan „Nem jön” került az eseményeire.' },
      { text: 'A 0 Ft-os tagdíj „Mentes”, nem számít tartozásnak; a lejátszott, de eredmény nélküli meccsek „Eredmény hiányzik” jelölést kapnak.' },
      { text: 'Telefonon a Játékosok oldal ritkábban használt gombjai a „Továbbiak” menübe kerültek, és az Utazások oldal sem lóg ki a képernyőből.' },
    ],
  },
  {
    date: '2026-10-21',
    title: 'AI asszisztens',
    audience: 'staff',
    requiresAi: true,
    items: [
      { text: 'Az edzésterv szerkesztőjében a „Terv AI-val” gombbal pár adatból kész edzéstervet kapsz, a klub gyakorlattárából válogatva.', feature: 'ai-edzesterv' },
      { text: 'A gyakorlatoknál és a tervblokkoknál a „Rajz AI-val” a leírásból megrajzolja az ábrát.', feature: 'ai-rajz' },
      { text: 'Az üzenőfalon a „Megírás AI-val” pár kulcsszóból kész bejelentést ír, vagy átfogalmazza a beírt szöveget.', feature: 'ai-bejelentes' },
      { text: 'A mérkőzés ablakában a „Beszámoló” gomb az eredményből és a gólszerzőkből beszámolót ír a szülőknek vagy a Facebookra.', feature: 'ai-beszamolo' },
      { text: 'Új menüpont: AI asszisztens. Itt kérdezhetsz a klub adataira, és heti összefoglalót kérhetsz a csapatodról.', feature: 'ai-kerdesek' },
      { text: 'A heti összefoglaló kiemeli, kire kell figyelni (sok hiányzás, magas terhelés, kevés játékperc), és javaslatot tesz a jövő hétre.', feature: 'ai-heti-osszefoglalo' },
      { text: 'A játékos adatlapján az „Értékelés AI-val” fejlődési értékelést ír a megfigyeléseidből és a statisztikákból.', feature: 'ai-ertekeles' },
      { text: 'Új játékosnál a „Kitöltés dokumentumból” a jelentkezési lap fotójából tölti ki az adatokat (ha az admin engedélyezi).', feature: 'ai-kitoltes' },
      { text: 'Az admin a Rendszerbeállításokban kapcsolhatja be az AI-t, választhat modellt és állíthat napi korlátot. Az AI a játékosok nevét nem látja, csak jelölést.', feature: 'ai-beallitasok' },
    ],
  },
  {
    date: '2026-10-18',
    title: 'Elfelejtett jelszó, korosztály az eseményeknél',
    items: [
      { text: 'A belépő oldalon az „Elfelejtetted a jelszavad?” linkkel új jelszót kérhetsz: emailben kapsz egy linket, amellyel magad állítod be, és utána rögtön be is lépsz. A link 1 óráig érvényes, és csak egyszer használható.' },
      { text: 'A családi appban minden eseménynél látszik a korosztály is (pl. „Edzés · U9”): a kezdőlapon, a Programban és az elmaradó események jelzésénél.' },
    ],
  },
  {
    date: '2026-10-16',
    title: 'Lövés-nyíl és teljes visszajelzési lista',
    items: [
      { text: 'Az edzésterv rajzolójában a nyilak között megjelent a „Lövés”: dupla szárú nyíl, ahogy a taktikai ábrákon szokás.' },
      { text: 'Az esemény „Visszajelzések” listájában a korosztály minden játékosa szerepel, az is, akinek csak szülői fiókja van; így látszik, kik nem válaszoltak még.' },
    ],
  },
  {
    date: '2026-10-11',
    title: 'Megújult pályafoglaltság és tagdíj nézet',
    items: [
      { text: 'A Pálya foglaltság oldal telefonon időrendi listát mutat a szabad sávokkal, asztalon áttekinthető idővonalat; a hétsávból egy koppintással váltasz napot.' },
      { text: 'A szülők a tagdíjat havi bontásban és éves összesítőben is látják, gyerek és állapot szerint szűrve, egy rövid statisztikával.' },
      { text: 'A családi app „Én” füle „Profil” lett.' },
      { text: 'Két keresztnévnél a gyereket az első keresztnevén szólítjuk (pl. „Finián Bálint József” → Bálint).' },
    ],
  },
  {
    date: '2026-10-11',
    title: 'Ismétlődő események, pályarész-nevek, utazás az eseménnyel',
    items: [
      { text: 'Új eseménynél rögtön utazást is szervezhetsz: járművek és megállók indulási idővel, ismétlődésnél minden alkalomra.', feature: 'fuvar-jelzes' },
      { text: 'Kerettel rendelkező meccsnél az utazást csak a keret tagjai látják, és csak ők jelezhetnek; keret nélkül mindenki.' },
      { text: 'Az eseményűrlap telefonon is kényelmes: szakaszokra bontva, névjavaslattal, gyors időtartam-gombokkal, a gombok mindig látszanak.' },
      { text: 'Ha az app még nincs a telefon kezdőképernyőjén, a főoldalon egy „Letöltés” gomb segít kitenni.' },
      { text: 'A játékos szerkesztésénél a játékos- és szülőfiók névre vagy email címre kereshető, nem kell több száz elemes listából választani.' },
      { text: 'Az ismétlődő esemény minden alkalma létrejön (legfeljebb 6 hónapra előre), mindegyik saját visszajelzéssel.' },
      { text: 'A pálya részei elnevezhetők (pl. „Kapu felőli”); a foglalásnál és a foglaltságnál ez a név látszik.', feature: 'palyareszek-neve' },
      { text: 'Autó felajánlásakor az indulás ideje helyszín nélkül is megadható és módosítható.' },
      { text: 'A jelentkezések, az iskolák, a tagdíjak és a pályák kezelése az adminra tartozik; az edző a pályák foglaltságát látja.' },
      { text: 'A főoldali sportorvosi figyelmeztetés az edzőnek csak a saját korosztályai játékosait mutatja.' },
      { text: 'A jobb alsó sarokban lebegő + gomb megszűnt; eseményt és játékost a saját oldalukon lehet felvenni.' },
    ],
  },
  {
    date: '2026-10-09',
    title: 'Letisztult főoldal, célzott tagdíj, bejegyzés eseményhez',
    items: [
      { text: 'Új főoldal: a közelgő események napok szerint, minden kártya egy kattintással megnyitja az eseményt; a teendők egy tömör listában, csak ha van mit tenni.' },
      { text: 'A tagdíjak generálása korosztályra vagy kiválasztott játékosokra is megy, előnézettel; 0 Ft-os tagdíj nem készül.', feature: 'tagdij-generalas' },
      { text: 'Fizetési emlékeztető korosztálynak vagy kiválasztott játékosoknak, push értesítésben és/vagy emailben.', feature: 'tagdij-emlekezteto' },
      { text: 'Az üzenőfal-bejegyzés eseményhez köthető, és az esemény oldalán is látszik.', feature: 'uzenofal-esemenyhez' },
      { text: 'Az Utazások oldalon minden közelgő esemény ott van (edzés, meccs, egyéb), típus szerint szűrhetően.' },
      { text: 'A Játékosok táblázat már nem lóg ki oldalra: a ritkábban kellő adatok a név alá kerülnek.' },
      { text: 'Edzőként is látszik és választható a játékoshoz kötött játékos- és szülőfiók.' },
      { text: 'A bejelentkezés és a regisztráció hibaüzenetei magyarul jelennek meg.' },
    ],
  },
  {
    date: '2026-10-09',
    title: 'Keret a meccsre, egyszerűbb visszajelzés',
    items: [
      { text: 'Mérkőzés létrehozásakor bejelölhető, hogy futsal, és opcionálisan megadható a keret: a meccset mindenki látja, de csak a keret tagjai jelezhetnek vissza.', feature: 'futsal-merkozes' },
      { text: 'Az esemény tetején gyerekenként egy kártya: jön-e, hogyan jut el (fuvar kell / saját maga megy, és ha viszed, „Tudok vinni valakit”), és ha jön, a kikérő.' },
      { text: 'Ha a gyerek utazását valaki módosítja, a hozzá kötött többi szülő és a játékos is értesítést kap.' },
      { text: 'Az utazás beosztásáról akkor is megy értesítés, ha az eseménynél az értesítések ki vannak kapcsolva.' },
      { text: 'Az utazás szerkesztőjében már nem látszanak azok, akik jelezték, hogy nem jönnek.' },
    ],
  },
  {
    date: '2026-10-09',
    title: 'Új felület a szülőknek és játékosoknak',
    items: [
      {
        text: 'A szülők és a játékosok telefonra szabott appot kapnak: Kezdőlap, Program, Hírek, Én - a stáb eszközei és a statisztikák nélkül.',
        feature: 'csaladi-felulet',
      },
      {
        text: 'A kezdőlapon a következő program, rögtön jelezhető, hogy jön-e a gyerek; teendő csak akkor látszik, ha tényleg van.',
        feature: 'csaladi-felulet',
      },
      {
        text: 'A Program egy listában mutat mindent; az esemény oldalán térkép, utazás és eredmény.',
        feature: 'csaladi-program',
      },
      { text: 'Több gyereknél mindegyikükre külön lehet jelezni, hogy jön-e.', feature: 'csaladi-program' },
      { text: 'A hiányzás bejelentése és a gyerek adatai az „Én” fülön, gyerekenként.', feature: 'gyerek-adatlap' },
    ],
  },
  {
    date: '2026-10-09',
    title: 'Fuvarszervezés a szülőkkel, futsal',
    items: [
      {
        text: 'A szülő az eseménynél jelzi, hogy a gyerekének fuvar kell, maga megy, vagy melyik megállóban száll fel a buszra.',
        feature: 'fuvar-jelzes',
      },
      {
        text: 'Aki autóval megy, felajánlhatja a szabad helyeit, és beültetheti a csapat gyerekeit - a szülők értesítést kapnak, ki viszi a gyereküket.',
        feature: 'fuvar-jelzes',
      },
      {
        text: 'Edzésekhez is lehet utazást szervezni, és egy kattintással átmásolni a következő edzésekre.',
        feature: 'utazas-edzesre',
      },
      { text: 'Futsal mérkőzésnél a felállás 4+1 fővel nyílik, nem 11-gyel.', feature: 'futsal-merkozes' },
    ],
  },
  {
    date: '2026-10-06',
    title: 'Gyorsabb munka nagy létszámmal',
    items: [
      { text: 'Az IFA oldal a Profilban mentett belépést használja - nem kell újra beírni.', feature: 'ifa-mentett-belepes' },
      { text: 'Gyorsszűrők és rendezés a Játékosok listában, 800 játékosnál is gyorsan.', feature: 'jatekos-gyorsszurok' },
      { text: 'Sportorvosi ellenőrzés fülekkel és kereséssel; a közös dátum egy lépésben menti el akár több száz játékosnak.', feature: 'sportorvosi-csoportos' },
      { text: 'Tagdíjak: korosztály-szűrő és csoportos „Fizetve” jelölés, villogás nélkül.', feature: 'tagdij-csoportos' },
      { text: 'Felhasználók keresése és szűrése; a gyerekek gépeléssel választhatók ki.', feature: 'felhasznalo-kereses' },
      { text: 'Javítva: a tömeges meghívónál szűrésváltás után nem maradnak ki a már beírt email címek.' },
      { text: 'Javítva: az edző, akinek a gyereke is a klubban játszik, ismét látja a teljes tagdíjlistát.' },
    ],
  },
  {
    date: '2026-10-06',
    title: 'Sportorvosi frissítés IFA-ból',
    items: [
      {
        text: 'A még érvényes sportorvosival rendelkező játékosok dátuma is frissíthető az IFA-ból - pl. ha a lejárat előtt elmentek az új vizsgálatra.',
        feature: 'sportorvosi-figyelmeztetes',
      },
    ],
  },
  {
    date: '2026-10-06',
    title: 'Funkciók és újdonságok',
    items: [
      {
        text: 'Új oldal, ahol minden funkció egy helyen látható, és innen nyomon követheted az újdonságokat is.',
        feature: 'funkciok-ujdonsagok',
      },
    ],
  },
  {
    date: '2026-10-05',
    title: 'Tanuló mód és Súgó',
    items: [
      { text: 'Bekapcsolható tanuló mód: rövid útmutató és bemutató animáció minden oldal tetején.', feature: 'tanulo-mod' },
      { text: 'Súgó oldal minden modul leírásával, kereshetően.', feature: 'tanulo-mod' },
    ],
  },
  {
    date: '2026-10-05',
    title: 'Nagy őszi frissítés',
    items: [
      { text: 'Online jelentkezési űrlap új játékosoknak, elbírálással.', feature: 'nyilvanos-jelentkezes' },
      { text: 'A szülő bejelentheti, ha a gyerek beteg vagy sérült.', feature: 'tavollet' },
      { text: 'Kapcsolattartók és egészségügyi tudnivalók a játékosoknál.', feature: 'kapcsolattartok' },
      { text: 'Naptár a telefonodon: az események a saját naptáradban is megjelennek.', feature: 'ical-feed' },
      { text: 'Játékosok importja és exportja Excelben, jelenléti ív letöltése.', feature: 'excel-import-export' },
      { text: 'Tagdíj emlékeztető a tartozóknak, akár havonta automatikusan.', feature: 'tagdij-emlekezteto' },
      { text: 'Edzés utáni visszajelzés a játékosoktól.', feature: 'edzes-visszajelzes' },
      { text: 'Mentett felállások és mezszám javaslat a mérkőzéseknél.', feature: 'mentett-felallasok' },
      { text: 'Mindenki maga választhatja ki, milyen emaileket kér.', feature: 'email-beallitasok' },
      { text: 'A sportorvosi figyelmeztetés ideje klubonként állítható.', feature: 'sportorvosi-ablak' },
    ],
  },
  {
    date: '2026-10-02',
    title: 'Utazásszervezés és iskolák',
    items: [
      { text: 'Utazások szervezése járművekkel, utasokkal és élő helyszámlálással.', feature: 'utazasszervezes' },
      { text: 'Iskolák nyilvántartása, kikérők és igazolások küldése az iskoláknak.', feature: 'iskolak' },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Felállás szerkesztő',
    items: [
      { text: 'Formációk, pályanézet, cserék - a felállás húzással állítható össze.', feature: 'felallas-szerkeszto' },
    ],
  },
  {
    date: '2026-09-29',
    title: 'Edzések',
    items: [
      { text: 'Jelenlét rögzítése az edzéseken.', feature: 'jelenlet' },
      { text: 'Edzésterv, gyakorlattár rajztáblával és edzéssablonok.', feature: 'gyakorlattar' },
      { text: 'Edzéslátogatottsági statisztika.', feature: 'edzes-latogatottsag' },
    ],
  },
  {
    date: '2026-09-28',
    title: 'Emlékeztetők és Portero',
    items: [
      { text: 'Eseményemlékeztetők, a klub alapértelmezett beállításaival.', feature: 'emlekeztetok' },
      { text: 'Játékos inaktiválása törlés helyett.', feature: 'jatekos-inaktivalas' },
      { text: 'Állítható, ki írhat és ki szólhat hozzá az üzenőfalon.', feature: 'uzenofal-jogok' },
      { text: 'Az alkalmazás új neve: Portero.' },
    ],
  },
  {
    date: '2026-09-07',
    title: 'Sportorvosi és IFA',
    items: [
      { text: 'Figyelmeztetés a 60 napon belül lejáró sportorvosi igazolásokra.', feature: 'sportorvosi-figyelmeztetes' },
      { text: 'Helyben felvett játékos összekötése az IFA-val, sportorvosi dátumok frissítése.', feature: 'ifa-import' },
    ],
  },
  {
    date: '2026-09-06',
    title: 'Vendégek',
    items: [
      { text: 'Vendég szerepkör gondnokoknak, kitmanagereknek, külsős csapatoknak.', feature: 'vendeg-szerepkor' },
      { text: 'A vendégek pályát foglalhatnak, adminisztrátori jóváhagyással.', feature: 'vendeg-foglalas' },
    ],
  },
  {
    date: '2026-09-05',
    title: 'Üzenőfal',
    items: [{ text: 'Közlemények a klubnak és a korosztályoknak.', feature: 'uzenofal' }],
  },
  {
    date: '2026-09-03',
    title: 'Több szerepkör, meghívók',
    items: [
      { text: 'Egy fiókban több szerepkör, pl. edző és szülő egyszerre.', feature: 'tobb-szerepkor' },
      { text: 'Szülők tömeges meghívása.', feature: 'tomeges-meghivo' },
      { text: 'Önregisztráció adminisztrátori jóváhagyással.', feature: 'onregisztracio' },
      { text: 'Email napló a kiküldött levelekről.', feature: 'email-naplo' },
    ],
  },
  {
    date: '2026-08-25',
    title: 'Push értesítések',
    items: [
      { text: 'Értesítések telefonra és gépre, kipróbálható beállítással.', feature: 'push-ertesitesek' },
      { text: 'Új fiók meghívóval.', feature: 'meghivok' },
    ],
  },
  {
    date: '2026-07-28',
    title: 'Pályarész foglalás',
    items: [
      { text: 'Az eseményeknél kijelölhető, a pálya melyik részét használja a csapat.', feature: 'palyaresz-foglalas' },
      { text: 'A mérkőzés automatikusan létrejön a mérkőzés típusú eseményből.', feature: 'merkozes-eredmenyek' },
    ],
  },
  {
    date: '2026-07-24',
    title: 'Mobil felület',
    items: [{ text: 'Telefonra szabott felület alsó menüvel, telepíthető alkalmazásként.', feature: 'telepitheto-app' }],
  },
  {
    date: '2026-04-21',
    title: 'Gyorsabb munka',
    items: [
      { text: 'Keresés mindenben (⌘K), gyors hozzáadás gomb.', feature: 'globalis-kereses' },
      { text: 'Szerepkörre szabott kezdőlap.', feature: 'attekinto' },
    ],
  },
  {
    date: '2026-04-06',
    title: 'Pályafoglaltság',
    items: [{ text: 'Heti és napi foglaltsági nézet pályánként.', feature: 'foglaltsag' }],
  },
  {
    date: '2026-04-04',
    title: 'Tagdíjak és arculat',
    items: [
      { text: 'Tagdíj nyilvántartás automatikus generálással, szülői nézettel.', feature: 'tagdij' },
      { text: 'Saját klubnév, címer és szín.', feature: 'arculat' },
    ],
  },
];

/** A legutóbbi kiadás dátuma - ehhez mérjük, látta-e már a felhasználó az újdonságokat. */
export const latestReleaseDate = releases[0]?.date ?? '';
